/**
 * SenderAccountLink (KAN-645)
 *
 * Compact chip of OUR Telegram account next to the Telegram badge: avatar
 * (picture or initials) plus the short alias (label from Settings, e.g. "RU-2").
 * The full profile name is in the hover hint. The whole chip links to
 * Settings > Telegram, where this account is scrolled into view and highlighted.
 * KAN-811: when Settings are closed to the user (MANAGER role) the chip is
 * plain text, not a link.
 */
import type { MouseEvent, ReactNode } from 'react'
import { Link, useInRouterContext } from 'react-router-dom'
import { SenderAvatar } from '@/components/messages/SenderAvatar'
import {
  senderAccountAlias,
  senderAccountHint,
  senderAccountName,
  senderAccountSettingsHref,
  senderAccountUsername,
  type SenderAccountDisplay,
} from '@/lib/senderAccount'
import { cn } from '@/lib/utils'
import { useCanOpenPath } from '@/hooks/useApiAccess'

export interface SenderAccountLinkAccount extends SenderAccountDisplay {
  account_id?: string | null
}

interface SenderAccountLinkProps {
  account: SenderAccountLinkAccount
  /** Extra text before the alias in the hint (e.g. "approximate" note). */
  hintPrefix?: string
  /** Rendered before the avatar inside the chip. */
  prefix?: ReactNode
  testId?: string
  className?: string
}

/** Keeps a click on the chip from also triggering the row it sits in. */
function stop(event: MouseEvent) {
  event.stopPropagation()
}

export function SenderAccountLink({
  account,
  hintPrefix,
  prefix,
  testId = 'sender-account-link',
  className,
}: SenderAccountLinkProps) {
  const inRouter = useInRouterContext()
  const canOpenSettings = useCanOpenPath(senderAccountSettingsHref('').split('?')[0])
  const alias = senderAccountAlias(account)
  if (!alias) return null

  const name = senderAccountName(account)
  const hint = [hintPrefix, senderAccountHint(account) || alias]
    .filter(Boolean)
    .join('. ')
  const accountId = (account.account_id ?? '').trim()
  const chipClass = cn(
    'inline-flex min-w-0 max-w-full items-center gap-1 rounded-full text-xs',
    className
  )
  const content = (
    <>
      {prefix}
      <SenderAvatar
        avatarUrl={account.avatar_url}
        name={name || senderAccountUsername(account.username) || alias}
      />
      <span className="truncate">{alias}</span>
    </>
  )

  if (!accountId || !canOpenSettings) {
    return (
      <span data-testid={testId} title={hint} className={chipClass}>
        {content}
      </span>
    )
  }

  const href = senderAccountSettingsHref(accountId)
  const linkClass = cn(
    chipClass,
    'hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
  )
  const label = `Аккаунт Telegram ${hint}: открыть в настройках`
  return inRouter ? (
    <Link
      to={href}
      data-testid={testId}
      title={hint}
      aria-label={label}
      onClick={stop}
      className={linkClass}
    >
      {content}
    </Link>
  ) : (
    <a
      href={href}
      data-testid={testId}
      title={hint}
      aria-label={label}
      onClick={stop}
      className={linkClass}
    >
      {content}
    </a>
  )
}
